import {
    Button,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DropdownMenuItem,
    Spinner,
} from "frontend-components";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import { subject } from "@casl/ability";
import { useAbility } from "@casl/react";

import { useTask, useTaskOccurrences, useDeleteTask } from "@dashboard/hooks/useTaskQuery";
import { useHseInstance } from "@dashboard/hooks/useHseInstance";
import {
    pickLatestOccurrence,
    withOccurrence,
    type TaskWithOccurrence,
} from "@dashboard/utils/taskWithOccurrence";
import { Can, TaskAbilityContext } from "@dashboard/casl/taskAbility";
import { ConfirmDeleteDialog } from "@dashboard/components/common/ConfirmDeleteDialog";

import { ConfirmDiscardDialog } from "../ConfirmDiscardDialog";

import { TaskViewBody } from "./TaskViewBody";
import { TaskFormBody, type TaskFormBodyHandle } from "./TaskFormBody";
import { useTaskLinks } from "./useTaskLinks";

interface TaskModalLoadedViewProps {
    mode: "view" | "edit";
    taskId: number;
    onClose: () => void;
}

export function TaskModalLoadedView({ mode, taskId, onClose }: TaskModalLoadedViewProps) {
    const { data: backendTask, isLoading: isLoadingTask, error } = useTask(taskId);
    const { data: occurrencesData, isLoading: isLoadingOccurrences } = useTaskOccurrences({
        taskId,
    });

    const isLoading = isLoadingTask || isLoadingOccurrences;
    const occurrence = useMemo(
        () => pickLatestOccurrence(occurrencesData?.data),
        [occurrencesData?.data],
    );

    const task = useMemo(() => {
        if (!backendTask || !backendTask.id) return null;
        return withOccurrence(backendTask, occurrence);
    }, [backendTask, occurrence]);

    if (isLoading || error || !task) {
        return (
            <TaskModalStatusView
                isLoading={isLoading}
                errorMessage={error?.message}
                taskId={taskId}
                onClose={onClose}
            />
        );
    }

    return <TaskModalLoadedContent mode={mode} task={task} onClose={onClose} />;
}

function TaskModalStatusView({
    isLoading,
    errorMessage,
    taskId,
    onClose,
}: {
    isLoading: boolean;
    errorMessage?: string;
    taskId: number;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    return (
        <Dialog
            open
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
        >
            <DialogContent className="bg-compendia-card max-h-[90vh] w-[calc(100vw-2rem)] sm:max-w-[700px] overflow-y-auto overflow-x-hidden text-compendia-foreground">
                <DialogHeader className="-mx-6 pl-6 pr-24 pb-4 border-b border-compendia-border min-w-0">
                    <DialogTitle className="text-lg font-bold text-compendia-foreground leading-tight overflow-hidden text-ellipsis whitespace-nowrap">
                        {t("common.activity")}
                    </DialogTitle>
                </DialogHeader>

                {isLoading ? (
                    <div className="flex items-center justify-center py-10">
                        <Spinner className="size-6" />
                    </div>
                ) : (
                    <div className="flex flex-col gap-3 py-6">
                        <h2 className="text-h3 font-medium">{t("common.taskNotFound")}</h2>
                        <p className="text-text-secondary">
                            {errorMessage || `${t("common.couldNotFindTaskWithId")}: ${taskId}`}
                        </p>
                        <div className="flex justify-end">
                            <Button variant="outline" onClick={onClose}>
                                {t("common.close")}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function TaskModalLoadedContent({
    mode,
    task,
    onClose,
}: {
    mode: "view" | "edit";
    task: TaskWithOccurrence;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    const { instanceId } = useHseInstance();
    const ability = useAbility(TaskAbilityContext);
    const navigate = useNavigate();
    const formRef = useRef<TaskFormBodyHandle | null>(null);
    const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [pendingRemoveLinkId, setPendingRemoveLinkId] = useState<number | null>(null);
    const deleteMutation = useDeleteTask();

    const canUpdate = ability.can("update", subject("Task", task));
    const canDelete = ability.can(
        "delete",
        subject("Task", { instanceId, createdById: task.createdById }),
    );

    const links = useTaskLinks({
        initialLinks: task.links ?? [],
        isEditMode: true,
        taskId: task.id,
        onLocalChange: () => formRef.current?.markDirty(),
    });

    const isFormDirty = () => mode === "edit" && (formRef.current?.isDirty() ?? false);

    const requestClose = () => {
        if (isFormDirty()) {
            setConfirmDiscardOpen(true);
            return;
        }
        onClose();
    };

    const goToEdit = () => {
        void navigate({
            to: ".",
            search: (prev) => ({ ...prev, task: task.id, edit: 1 }),
        });
    };

    const goToView = () => {
        void navigate({
            to: ".",
            search: (prev) => ({ ...prev, task: task.id, edit: undefined }),
        });
    };

    const handleDelete = async () => {
        try {
            await deleteMutation.mutateAsync(task.id);
            onClose();
        } catch {
            // Error surfaced via deleteMutation.error; keep modal open so the user can retry.
        }
    };

    const confirmRemoveLink = () => {
        if (pendingRemoveLinkId !== null) {
            links.remove(pendingRemoveLinkId);
            setPendingRemoveLinkId(null);
        }
    };

    return (
        <>
            <Dialog
                open
                onOpenChange={(open) => {
                    if (!open) requestClose();
                }}
            >
                <DialogContent
                    className="bg-compendia-card max-h-[90vh] w-[calc(100vw-2rem)] sm:max-w-[700px] overflow-y-auto overflow-x-hidden text-compendia-foreground"
                    menuLabel={t("common.openMenu")}
                    menu={
                        mode === "view" && (canUpdate || canDelete) ? (
                            <>
                                <Can I="update" this={subject("Task", task)}>
                                    <DropdownMenuItem onClick={goToEdit}>
                                        <FontAwesomeIcon icon={faPencil} />
                                        {t("common.edit")}
                                    </DropdownMenuItem>
                                </Can>
                                <Can
                                    I="delete"
                                    this={subject("Task", {
                                        instanceId,
                                        createdById: task.createdById,
                                    })}
                                >
                                    <DropdownMenuItem
                                        variant="destructive"
                                        onClick={() => setConfirmDeleteOpen(true)}
                                    >
                                        <FontAwesomeIcon icon={faTrashCan} />
                                        {t("common.delete")}
                                    </DropdownMenuItem>
                                </Can>
                            </>
                        ) : undefined
                    }
                >
                    <DialogHeader className="-mx-6 pl-6 pr-24 pb-4 border-b border-compendia-border min-w-0">
                        <DialogTitle className="text-lg font-bold text-compendia-foreground leading-tight overflow-hidden text-ellipsis whitespace-nowrap">
                            {mode === "edit" ? t("task.editActivity") : task.title}
                        </DialogTitle>
                    </DialogHeader>

                    {mode === "view" ? (
                        <TaskViewBody task={task} onClose={onClose} />
                    ) : (
                        <TaskFormBody
                            key={`edit-${task.id}`}
                            ref={formRef}
                            task={task}
                            links={links}
                            onRequestRemoveLink={setPendingRemoveLinkId}
                            onCancel={() => {
                                if (isFormDirty()) {
                                    setConfirmDiscardOpen(true);
                                    return;
                                }
                                goToView();
                            }}
                            onSaved={() => goToView()}
                            onRequestDelete={() => setConfirmDeleteOpen(true)}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <ConfirmDiscardDialog
                open={confirmDiscardOpen}
                onOpenChange={setConfirmDiscardOpen}
                onConfirm={() => {
                    setConfirmDiscardOpen(false);
                    if (mode === "edit") {
                        goToView();
                    } else {
                        onClose();
                    }
                }}
            />

            <ConfirmDeleteDialog
                open={confirmDeleteOpen}
                onOpenChange={setConfirmDeleteOpen}
                onConfirm={handleDelete}
                title={t("common.deleteActivity")}
                description={t("common.confirmDeleteActivity")}
            />

            <ConfirmDeleteDialog
                open={pendingRemoveLinkId !== null}
                onOpenChange={(open) => {
                    if (!open) setPendingRemoveLinkId(null);
                }}
                onConfirm={confirmRemoveLink}
                title={t("common.removeLink")}
                description={t("common.confirmRemoveLink")}
            />
        </>
    );
}
